import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Input,
  Button,
  Stack,
  Select,
  HStack,
  VStack,
  Portal,
  IconButton,
  Container,
  createListCollection,
} from "@chakra-ui/react";
import { IoCloseSharp } from "react-icons/io5";
import { Toaster, toaster } from "../components/ui/toaster";
import { useCustomCategories } from "../context/CustomCatgoriesContext";
import { useSavingGoals } from "../context/SavingGoalsContext";
import { formatEuro } from "../utils/helperFunctions";

type SelectTypes = {
  label: string;
  value: string;
};

export const SavingGoalsPage = () => {
  const { categories: customCategories } = useCustomCategories();
  const { savingGoals, addGoal, removeGoal } = useSavingGoals();
  const [title, setTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [amount, setAmount] = useState("");

  const validCategoryValues = customCategories.map((c) => c.value);

  const filteredGoals = savingGoals.filter((g) =>
    validCategoryValues.includes(g.category)
  );

  const categoryCollection = createListCollection({
    items: customCategories.map((cat) => ({
      label: cat.label,
      value: cat.value,
    })),
  });

  const handleSave = () => {
    if (!title.trim()) {
      toaster.create({
        description: "Bitte gib eine kurze Beschreibung für das Sparziel ein.",
        type: "error",
        placement: "top-end",
      });
      return;
    }
    if (!amount) {
      toaster.create({
        description: "Bitte gib einen Betrag ein.",
        type: "error",
        placement: "top-end",
      });
      return;
    }
    if (!selectedCategory) {
      toaster.create({
        description: "Bitte wähle eine Kategorie aus.",
        type: "error",
        placement: "top-end",
      });
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      return;
    }

    addGoal({
      category: selectedCategory,
      amount: parsedAmount,
      title: title.trim() !== "" ? title.trim() : undefined,
    });

    toaster.create({
      description: "Sparziel erfolgreich gespeichert",
      type: "success",
      placement: "top-end",
    });

    // Reset
    setTitle("");
    setSelectedCategory("");
    setAmount("");
  };

  const handleRemove = (id: string) => {
    removeGoal(id);
  };

  return (
    <Container maxW="lg" py={10} pb={{ base: 24, md: 10 }}>
      <Heading textAlign="center">Sparziele</Heading>
      <Toaster />
      <Box
        maxW="lg"
        mx="auto"
        mt={10}
        p={6}
        borderWidth="1px"
        borderRadius="md"
        boxShadow="md"
      >
        <Heading size="lg" mb={2} textAlign="center">
          Sparziele anlegen
        </Heading>
        <Text mb={6} textAlign="justify">
          Lege ein langfristige Sparziele für Kategorien fest, z. B. für Urlaub,
          Hobbys oder andere Anschaffungen.
        </Text>
        <Stack spaceY={4}>
          <Input
            type="text"
            required
            placeholder="z.B. Dell Laptop Studium"
            borderColor="gray"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Select.Root
            width={"100%"}
            size="sm"
            collection={categoryCollection}
            onChange={(e: any) => setSelectedCategory(e.target.value)}
            required
          >
            <Select.HiddenSelect />
            <Select.Control>
              <Select.Trigger borderColor="gray">
                <Select.ValueText placeholder="Kategorie wählen" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content>
                  {categoryCollection.items.map((item: SelectTypes) => (
                    <Select.Item item={item} key={item.value}>
                      {item.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>

          <Input
            type="number"
            inputMode="decimal"
            pattern="[0-9]*"
            placeholder="€ Zielbetrag"
            value={amount}
            borderColor="gray"
            onChange={(e) => setAmount(e.target.value)}
          />

          <Button colorScheme="teal" onClick={handleSave}>
            Speichern
          </Button>
        </Stack>

        {filteredGoals.length > 0 && (
          <Box mt={8}>
            <Heading size="md" mb={4}>
              Bestehende Sparziele
            </Heading>
            <Stack spaceY={3}>
              {filteredGoals.map((goal) => {
                // Kategorie-Label aus dem categories-Array holen
                const categoryLabel =
                  customCategories.find((c) => c.value === goal.category)
                    ?.label || goal.category;

                return (
                  <VStack align="stretch" key={goal.id}>
                    <HStack
                      justify="space-between"
                      p={3}
                      gap="2rem"
                      bg="gray.100"
                      borderRadius="md"
                    >
                      <Box maxW={150} overflowWrap="break-word">
                        {/* Wenn Titel vorhanden, zeige Titel + Kategorie, sonst nur Kategorie */}
                        {goal.title ? (
                          <>
                            <Text fontWeight="medium">{goal.title}</Text>
                            <Text fontSize="sm" color="gray.500">
                              Kategorie: {categoryLabel}
                            </Text>
                          </>
                        ) : (
                          <Text fontWeight="medium">{categoryLabel}</Text>
                        )}
                      </Box>

                      <Text fontWeight="bold" color="#fb9900">
                        {formatEuro(goal.amount)}
                      </Text>

                      <IconButton
                        size="sm"
                        aria-label="Löschen"
                        onClick={() => handleRemove(goal.id)}
                        colorScheme="red"
                        variant="ghost"
                      >
                        {/* @ts-ignore */}
                        <IoCloseSharp />
                      </IconButton>
                    </HStack>
                  </VStack>
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>
    </Container>
  );
};
